// src/components/views/NFTExplorerView.js
import React, { useEffect, useState } from 'react';
import { Row, Col, Form, Button, Collapse, Alert } from 'react-bootstrap';
import LazyLoad, { forceCheck } from 'react-lazyload';

const PROSPECT_IMG =
  'https://nft.sogni.ai/big/badge-fast-worker-prospect/001.jpg';

const NFTExplorerView = ({
  filteredNFTs,
  baseURI,              // still received but unused for Workers
  handleCardClick,
  searchQuery,
  setSearchQuery,
  accountCreated,
  networkFilter,
  setNetworkFilter,
  isSoldOut,
  remainingMints
}) => {
  const [isInfoExpanded, setIsInfoExpanded] = useState(false);

  useEffect(() => {
    // Filtering can move existing lazy placeholders into view without a scroll.
    forceCheck();
  }, [filteredNFTs]);

  return (
  <>
    <style>{`
      /* Improve placeholder contrast for the token ID input */
      .token-id-input::placeholder { color: #bbb; opacity: 1; }
    `}</style>
    {accountCreated && (
      <div
        style={{
          backgroundColor: '#292929',
          border: '1px solid #444',
          padding: '10px',
          borderRadius: '5px',
          marginBottom: '15px'
        }}
      >
        <strong>
          If you haven&apos;t, please mint your Prospect Worker NFT.
        </strong>
      </div>
    )}

    <Alert variant="info" className="mb-4">
      <strong>More GPUs? Use the same Sogni account.</strong>{' '}
      You can mint multiple Worker NFTs directly to your existing account, with no
      per-account limit while supply is available. Each NFT is paid separately in
      ETH on Base, plus gas.
      {remainingMints !== null && (
        <div className="mt-2">
          <strong>
            {remainingMints.toLocaleString()} {remainingMints === 1 ? 'mint' : 'mints'} left.
          </strong>
        </div>
      )}
    </Alert>

    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
      <Form.Group
        controlId="filter"
        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
      >
        <Form.Label className="mb-0">Status</Form.Label>
        <Form.Control
          as="select"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ backgroundColor: '#333', color: '#fff', width: '200px' }}
        >
          <option value="ALL">All</option>
          <option value="MINTED">Minted</option>
          <option value="AVAILABLE">Available</option>
        </Form.Control>
      </Form.Group>

      <Form.Group
        controlId="networkFilter"
        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
      >
        <Form.Label className="mb-0">Network</Form.Label>
        <Form.Control
          as="select"
          value={networkFilter}
          onChange={(e) => setNetworkFilter(e.target.value)}
          style={{ backgroundColor: '#333', color: '#fff', width: '200px' }}
        >
          <option value="ALL">All Networks</option>
          <option value="BASE">Base</option>
          <option value="ETHERLINK">Etherlink</option>
        </Form.Control>
      </Form.Group>
    </div>

    {/* Collapsible helpful information */}
    <div className="mb-4">
      <div
        onClick={() => setIsInfoExpanded(!isInfoExpanded)}
        style={{
          backgroundColor: '#292929',
          border: '1px solid #444',
          padding: '12px 15px',
          borderRadius: '5px',
          cursor: 'pointer',
          marginBottom: '10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          userSelect: 'none'
        }}
      >
        <strong>💡 How to Purchase NFTs & View NFT Details</strong>
        <span style={{ fontSize: '18px', transition: 'transform 0.3s', transform: isInfoExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
          ▼
        </span>
      </div>
      <Collapse in={isInfoExpanded}>
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '5px',
          padding: '18px 22px',
          marginTop: '10px'
        }}>
          {/* Purchase Options */}
          <div style={{ marginBottom: '18px' }}>
            <h6 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Purchase Options
            </h6>
            <p style={{ fontSize: '13px', lineHeight: '1.6', marginBottom: '0', opacity: 0.9 }}>
              Sign in with the Sogni account that will own your NFTs. Fund that
              account&apos;s wallet once with enough ETH on Base for all the NFTs
              you want, plus gas for each mint. Select an available NFT to see its
              price and mint it, then repeat for each additional NFT using the same
              account. No extra accounts or transfers are needed. You can also
              purchase from the secondary market on{' '}
              <a href="https://opensea.io/collection/sogni-prospect-worker" target="_blank" rel="noopener noreferrer" style={{ color: '#39ff14' }}>OpenSea</a>.
            </p>
          </div>

          {/* Collection Verification */}
          <div style={{ marginBottom: '18px' }}>
            <h6 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Verify Collection
            </h6>
            <p style={{ fontSize: '13px', lineHeight: '1.6', marginBottom: '0', opacity: 0.9 }}>
              Ensure NFTs belong to{' '}
              <a href="https://basescan.org/token/0x1B228dbB1989b96D6Fe989822EE25B979812Da29" target="_blank" rel="noopener noreferrer" style={{ color: '#39ff14' }}>
                Sogni Prospect Worker (P2WORK)
              </a>
              {' '}at <code style={{ backgroundColor: '#2a2a2a', padding: '2px 6px', borderRadius: '3px', fontSize: '11px', wordBreak: 'break-all' }}>0x1B228dbB1989b96D6Fe989822EE25B979812Da29</code> on Base. Beware of fake collections and check reputation scores before purchasing.
            </p>
          </div>

          {/* Token Lookup */}
          <div>
            <h6 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              View NFT Details
            </h6>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const raw = form.elements.tokenId?.value?.trim();
                const tokenId = parseInt(raw, 10);
                if (!Number.isNaN(tokenId) && tokenId >= 0) {
                  window.location.href = `https://nft.sogni.ai/nft/${tokenId}`;
                }
              }}
            >
              <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch', maxWidth: '350px' }}>
                <Form.Control
                  name="tokenId"
                  type="number"
                  min="0"
                  placeholder="Token ID"
                  className="token-id-input"
                  size="sm"
                  style={{ 
                    backgroundColor: '#2a2a2a', 
                    color: '#fff', 
                    fontSize: '13px',
                    border: '1px solid #444',
                    borderRadius: '4px'
                  }}
                />
                <Button 
                  type="submit" 
                  size="sm" 
                  style={{ 
                    whiteSpace: 'nowrap', 
                    fontSize: '13px',
                    backgroundColor: '#39ff14',
                    border: 'none',
                    color: '#000',
                    fontWeight: '600',
                    padding: '6px 16px',
                    borderRadius: '4px'
                  }}
                >
                  View NFT
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </Collapse>
    </div>

    {/* Separator */}
    <hr style={{ borderTop: '1px solid #444', margin: '20px 0' }} />

    {isSoldOut && filteredNFTs.length === 0 ? (
      <div>
        <p>
          Sorry, the initial mint of Fast Worker Prospect NFTs have all been
          purchased / awarded. Keep an eye on our Discord announcements channel
          for when the next batch may arrive and fill <a href="https://nft-signup.sogni.ai/" target="_blank" rel="noopener noreferrer">this form</a>
          {' '}for accelerated access!
        </p>
      </div>
    ) : filteredNFTs.length > 0 ? (
      <Row>
        {filteredNFTs.map((nft) => (
          <Col key={nft.tokenId} xs={6} sm={6} md={4} lg={3} className="mb-4">
            <div
              className="nft-card"
              style={{
                backgroundColor: '#222',
                color: '#fff',
                cursor: 'pointer'
              }}
              onClick={() => handleCardClick(nft)}
            >
              <div style={{ position: 'relative' }}>
                <LazyLoad height={200} offset={0} once>
                  {/* ⬇️  Single prospect artwork for every card */}
                  <img
                    src={PROSPECT_IMG}
                    alt={`Sogni Prospect Worker #${nft.tokenId}`}
                    style={{ width: '100%', borderRadius: '10px' }}
                  />
                </LazyLoad>
                <div className={`overlay ${nft.minted ? 'minted' : ''}`}>
                  {nft.minted ? 'MINTED' : 'AVAILABLE'}
                </div>
              </div>
              <div style={{ padding: '10px' }}>
                <h5 style={{ color: '#39ff14' }}>
                  Sogni Prospect Worker #{nft.tokenId}
                  {nft.chain && (
                    <>
                      <br />
                      <span>{nft.chain}</span>
                    </>
                  )}
                </h5>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    ) : null}
  </>
  );
};

export default NFTExplorerView;
